import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  APIHelper,
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacChipsComponent,
  CacDialogLayoutComponent,
  CacFieldComponent,
  CacFormBuilderComponent,
  CacIconComponent,
  CacSwitchComponent,
  formBuilder,
  ItemRecord,
} from '@controladad/ng-base';
import { formControl, FormControlExtended, formGroup, FormGroupExtended, Validators } from '@al00x/forms';
import { ProductTypeValues } from '../../../data';
import { AttributeApiService, IProductApiService } from '../../../services';
import {
  OrderedProductImage,
  OrderedProductVideo,
  ProductAttributeDetail,
  ProductEntity,
  RelatedProduct,
  VariantUpdate,
  VariantEntity,
  ProductAttribute,
  ProductDropdownItem,
  CategoryItem,
  BundleStockMode,
} from '@models';
import { BehaviorSubject, debounceTime, forkJoin, map, Observable, of, shareReplay, take, tap } from 'rxjs';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { CategoryTreeComponent, LazyDropdownComponent, LazyDropdownItem } from '../../../components';
import { Variation, VariationFormComponent } from './variation-form/variation-form.component';
import { openAttributeDialog } from '../attribute-dialog/attribute-dialog.component';
import {
  BundleComponentSelection,
  BundleVariantOption,
  createBundleComponentSelection,
  effectiveBundleComponentPrice,
  isBundleStockModeOptionSelected,
} from './bundle-product.helpers';


export type ProductDialogData = { apiService: IProductApiService; isAdmin: boolean; product?: ProductEntity; productId?: number; duplicate?: boolean };
export type ProductDialogResult = boolean;

type Specification = FormGroupExtended<{
  attributeId: FormControlExtended<number>;
  termId: FormControlExtended<number>;
}>;

type Attribute = FormGroupExtended<{
  attributeId: FormControlExtended<number>;
  termIds: FormControlExtended<number[]>;
}>;

interface ImportedProduct {
  productId: number;
  detail: ProductEntity | null; // null when loading
  variantId: string | null; // tracks by clientId or variationId
}

@Component({
  selector: 'lib-product-dialog',
  templateUrl: './product-dialog.component.html',
  styleUrl: './product-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    CacFieldComponent,
    AsyncPipe,
    CacSwitchComponent,
    CacButtonComponent,
    CacFormBuilderComponent,
    CacDialogLayoutComponent,
    CacChipsComponent,
    CacIconComponent,
    NgIf,
    ReactiveFormsModule,
    QuillModule,
    CategoryTreeComponent,
    VariationFormComponent,
    LazyDropdownComponent,
  ],
})
export class ProductDialogComponent
  extends CacBaseDialogComponent<ProductDialogData, ProductDialogResult>
  implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // ViewChild for the scrollable content container so we can reset scroll on step change
  @ViewChild('scrollContainer', { read: ElementRef }) private scrollContainerRef?: ElementRef<HTMLElement>;

  // Step management
  currentStep = signal<number>(0);
  stepValidationErrors = signal<string[]>([]);

  // Existing signals
  isVariable = signal<boolean>(false);
  isBundle = signal<boolean>(false);
  bundleStockMode = signal<BundleStockMode>(BundleStockMode.Manual);
  bundleComponents = signal<BundleComponentSelection[]>([]);
  isDerivedBundle = computed(
    () => this.isBundle() && this.bundleStockMode() === BundleStockMode.ComponentDerived,
  );
  calculatedBundleStock = computed<number | null | undefined>(() => {
    const components = this.bundleComponents();
    if (!this.isDerivedBundle() || components.length === 0 || components.some((component) => !component.stockLoaded)) {
      return undefined;
    }

    const manageableStocks = components.flatMap((component) => {
      const variant = component.variants.find((candidate) => candidate.id === component.componentVariantId);
      if (!variant?.isManageableStock) return [];
      return [Math.floor((variant.stock ?? 0) / component.quantity)];
    });

    return manageableStocks.length > 0 ? Math.min(...manageableStocks) : null;
  });
  bundleComponentPriceTotal = computed(() => Math.round(this.bundleComponents().reduce(
    (total, component) => total + component.unitPrice * component.quantity,
    0,
  ) * 100) / 100);
  specifications = signal<Specification[]>([]);
  attributes = signal<Attribute[]>([]);
  variations = signal<Variation[]>([]);
  submitError = signal<string | undefined>(undefined);

  expandedVariants: boolean[] = [];
  attributeItems$ = new BehaviorSubject<ItemRecord<number>[]>([]);
  termItemsCache: { [attributeId: number]: Observable<ItemRecord<number>[]> } = {};
  productImages$ = new BehaviorSubject<OrderedProductImage[]>([]);
  productVideos$ = new BehaviorSubject<OrderedProductVideo[]>([]);
  relatedProducts$ = new BehaviorSubject<RelatedProduct[]>([]);
  categories$ = this.data.apiService.getCategories().pipe(shareReplay(1));

  importedProducts = signal<ImportedProduct[]>([]);

  originalVariants: VariantEntity[] = [];

  // Dynamic step configuration based on product type
  steps = signal<{ label: string; key: string; }[]>([]);
  preOrderAvailableOn = formControl<string | null>(null);

  basicInfo = formBuilder({
    cols: 12,
    inputs: {
      englishName: {
        label: 'English Name',
        control: formControl<string>(undefined, Validators.required),
        colspan: 4,
      },
      arabicName: {
        label: 'Arabic Name',
        control: formControl<string>(undefined, Validators.required),
        colspan: 4,
      },
      type: {
        label: 'Product Type',
        control: formControl<number>(undefined, Validators.required),
        type: 'select',
        items: ProductTypeValues,
        clearable: false,
        searchable: false,
        colspan: 4,
      },
      brandId: {
        label: 'Brand',
        control: formControl<number>(undefined),
        type: 'select',
        items: this.data.apiService.getBrandItems(),
        colspan: 4,
      },
      slug: {
        label: 'Link name',
        control: formControl<string>(undefined, Validators.required),
        colspan: 4,
      },
      vendorId: {
        label: 'Vendor',
        control: formControl<number>(undefined, Validators.required),
        type: 'select',
        items: this.data.apiService.getAllVendors(),
        hidden: !this.data.isAdmin,
        colspan: 4,
      },
      status: {
        label: 'Status',
        control: formControl<boolean>(true),
        type: 'switch',
        colspan: 3,
      },
      isFeatured: {
        label: 'Is Featured',
        control: formControl<boolean>(false),
        type: 'switch',
        colspan: 3,
      },
      keepTop: {
        label: 'Keep Top',
        control: formControl<boolean>(false),
        type: 'switch',
        colspan: 3,
      },
      isPreOrder: {
        label: 'Pre-delivery',
        control: formControl<boolean>(false),
        type: 'switch',
        hidden: !this.data.isAdmin,
        colspan: 3,
      },
      limitedToOnePurchases: {
        label: 'Limited to one purchase',
        type: 'switch',
        control: formControl<boolean>(false),
        colspan: 3,
      },
    },
    onInit: (controls) => {
      controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.isVariable.set(value === 1);
        this.isBundle.set(value === 2);
      });

      // Auto-fill slug based on English name
      controls.englishName.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((englishName) => {
        if (englishName) {
          const currentSlug = controls.slug.value;
          // Only auto-fill if slug is empty or if it matches a previous auto-generated slug
          if (!currentSlug || this.isAutoGeneratedSlug(currentSlug, englishName)) {
            const slug = this.generateSlug(englishName);
            controls.slug.patchValue(slug);
          }
        } else {
          // If English name is cleared, clear the slug too (if it was auto-generated)
          const currentSlug = controls.slug.value;
          if (currentSlug && this.isAutoGeneratedSlug(currentSlug, '')) {
            controls.slug.patchValue('');
          }
        }
      });
    },
  });

  categoriesFormGroup = formGroup({
    primaryCategory: formControl<number>(undefined, Validators.required),
    secondaryCategories: formControl<number[]>(undefined),
  });

  // Separate reactive FormGroup for rich-text descriptions (not part of the formBuilder)
  descriptionsForm = formGroup({
    english: formControl<string>(undefined),
    arabic: formControl<string>(undefined),
  });

  showAddVideoDialog = () =>
    dialog$.input({
      title: 'New Video',
      addButtonText: 'Add',
      formBuilder: formBuilder({
        inputs: {
          url: {
            control: formControl<string>(undefined, Validators.required),
            label: "Video's Url",
          },
          order: {
            control: formControl<number>(1, Validators.required),
            label: 'Order',
          },
        },
      }),
    });

  // New media management methods
  addImage(): void {
    dialog$
      .productImage()
      .afterSubmit()
      .pipe(
        take(1),
        tap((result: any) => {
          this.productImages$.next([...this.productImages$.value, result]);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  activateRTL(editor: any) {
    // Apply RTL formatting and right alignment for Arabic editor
    try {
      editor.format('align', 'right');
      editor.format('direction', 'rtl');

      const length = typeof editor.getLength === 'function' ? editor.getLength() : 1;
      if (typeof editor.formatLine === 'function') {
        editor.formatLine(0, length, { direction: 'rtl', align: 'right' });
      }
      if (editor.root) {
        editor.root.setAttribute('dir', 'rtl');
        editor.root.style.direction = 'rtl';
        editor.root.style.textAlign = 'right';
      }
    } catch { }
  }

  removeImage(index: number): void {
    const images = this.productImages$.value;
    this.productImages$.next(images.filter((_, i) => i !== index));
    this.cdr.detectChanges();
  }

  editImageOrder(index: number): void {
    const image = this.productImages$.value[index];
    if (!image) return;

    dialog$.input({
      title: 'Edit Image Order',
      addButtonText: 'Update',
      formBuilder: formBuilder({
        inputs: {
          order: {
            control: formControl<number>(image.order, Validators.required),
            label: 'Order',
          },
        },
      }),
    })
      .afterSubmit()
      .pipe(
        take(1),
        tap((result: any) => {
          const images = [...this.productImages$.value];
          images[index] = { ...image, order: result.order };
          this.productImages$.next(images);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  downloadImage(url: string): void {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = this.getFilenameFromUrl(url) || 'image.jpg';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Error downloading image:', error);
      });
  }

  private getFilenameFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
      return filename || null;
    } catch {
      return null;
    }
  }

  addVideo(): void {
    this.showAddVideoDialog()
      .afterSubmit()
      .pipe(
        take(1),
        tap((result: any) => {
          this.productVideos$.next([...this.productVideos$.value, { order: result.order, url: result.url }]);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  removeVideo(index: number): void {
    const videos = this.productVideos$.value;
    this.productVideos$.next(videos.filter((_, i) => i !== index));
    this.cdr.detectChanges();
  }

  // Product dropdown configuration for related products and import
  productApiAdapter = this.data.apiService.getProductNamesAdapter();

  // Simple products adapter (type = 0)
  simpleProductApiAdapter = this.data.apiService.getProductNamesAdapter({
    default: {
      filters: [{ key: 'Types', value: [0], type: 'equal' }]
    }
  });

  bundleComponentApiAdapter = this.data.apiService.getProductNamesAdapter({
    default: {
      filters: [{ key: 'Types', value: [0, 1], type: 'equal' }]
    }
  });

  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
    image: product.productFirstImage,
  });

  // Related Products Management
  onRelatedProductsChange(selectedItem: LazyDropdownItem | LazyDropdownItem[] | null): void {
    if (!selectedItem) {
      return;
    }

    // Handle array case (though we use single selection, this ensures type compatibility)
    const item = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem;

    if (!item) {
      return;
    }

    // Load full product data to get the thumbnail
    this.data.apiService.get(item.id).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (product) => {
        const newRelatedProduct: RelatedProduct = {
          id: item.id,
          name: {
            english: product.name.english,
            arabic: product.name.arabic
          },
          thumbnail: product.images?.[0] || undefined
        };

        const currentProducts = this.relatedProducts$.value;
        this.relatedProducts$.next([...currentProducts, newRelatedProduct]);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading product:', error);
        snackbar$?.show?.('Failed to load product details');
      }
    });
  }

  // Get currently selected related product IDs for exclusion
  getSelectedRelatedProductIds(): number[] {
    return this.relatedProducts$.value.map(product => product.id);
  }

  removeRelatedProduct(productId: number): void {
    const currentProducts = this.relatedProducts$.value;
    const updatedProducts = currentProducts.filter(p => p.id !== productId);
    this.relatedProducts$.next(updatedProducts);
    this.cdr.detectChanges();
  }

  onBundleProductChange(selectedItem: LazyDropdownItem | LazyDropdownItem[] | null): void {
    const item = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem;
    if (!item) return;

    this.data.apiService.get(item.id).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (product) => {
        const selectedVariantIds = new Set(this.bundleComponents().map((component) => component.componentVariantId));
        const selection = createBundleComponentSelection(item.id, product, selectedVariantIds);
        if (!selection) {
          snackbar$?.show?.('All variants of this product are already in the bundle');
          return;
        }

        this.bundleComponents.update((components) => [...components, selection]);
        this.syncBundlePriceFromComponents();
      },
      error: () => snackbar$?.show?.('Failed to load product details'),
    });
  }

  getBundleExcludedProductIds(): number[] {
    return this.data.productId ? [this.data.productId] : [];
  }

  updateBundleComponentVariant(index: number, componentVariantId: number): void {
    if (this.bundleComponents().some((component, componentIndex) =>
      componentIndex !== index && component.componentVariantId === componentVariantId)) {
      snackbar$?.show?.('This variant is already in the bundle');
      return;
    }

    this.bundleComponents.update((components) => components.map((component, componentIndex) => {
      if (componentIndex !== index) return component;

      const variant = component.variants.find((candidate) => candidate.id === componentVariantId);
      return {
        ...component,
        componentVariantId,
        unitPrice: variant ? effectiveBundleComponentPrice(variant) : component.unitPrice,
      };
    }));
    this.syncBundlePriceFromComponents();
  }

  updateBundleComponentQuantity(index: number, quantity: number): void {
    this.bundleComponents.update((components) => components.map((component, componentIndex) =>
      componentIndex === index ? { ...component, quantity } : component));
    this.syncBundlePriceFromComponents();
  }

  updateBundleComponentUnitPrice(index: number, unitPrice: number): void {
    this.bundleComponents.update((components) => components.map((component, componentIndex) =>
      componentIndex === index ? { ...component, unitPrice } : component));
    this.syncBundlePriceFromComponents();
  }

  removeBundleComponent(index: number): void {
    this.bundleComponents.update((components) => components.filter((_, componentIndex) => componentIndex !== index));
    this.syncBundlePriceFromComponents();
  }

  private syncBundlePriceFromComponents(): void {
    const components = this.bundleComponents();
    const bundleVariation = this.variations()[0];
    if (!this.isBundle() || !bundleVariation || components.length === 0) return;
    if (components.some((component) =>
      !Number.isFinite(component.unitPrice)
      || !Number.isInteger(component.quantity)
      || component.quantity <= 0)) return;

    bundleVariation.form.patchValue({
      regularPrice: this.bundleComponentPriceTotal(),
      salePrice: null,
    });
  }

  setBundleStockMode(value: number): void {
    this.bundleStockMode.set(value as BundleStockMode);
  }

  isBundleStockModeSelected(option: BundleStockMode): boolean {
    return isBundleStockModeOptionSelected(this.bundleStockMode(), option);
  }

  bundleVariantLabel(variant: BundleVariantOption): string {
    return variant.itemCode ? `${variant.sku} (${variant.itemCode})` : variant.sku;
  }

  // Imported Products Management (for variant import)
  onImportedProductChange(selectedItem: LazyDropdownItem | LazyDropdownItem[] | null): void {
    if (!selectedItem) {
      return;
    }

    const item = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem;
    if (!item) {
      return;
    }

    // Check if already imported
    if (this.importedProducts().some(p => p.productId === item.id)) {
      snackbar$?.show?.('Product already imported');
      return;
    }

    // Add product with loading state
    this.importedProducts.update(products => [
      ...products,
      { productId: item.id, detail: null, variantId: null }
    ]);

    // Load and validate product
    this.data.apiService.get(item.id).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (product) => {
        // Validate simple product with variant
        if (product.type !== 0 || !product.variants || product.variants.length === 0) {
          this.importedProducts.update(products => products.filter(p => p.productId !== item.id));
          snackbar$?.show?.(
            product.type !== 0 ? 'Only simple products can be imported' : 'Product has no variant data'
          );
          return;
        }

        // Update with loaded product details
        this.importedProducts.update(products =>
          products.map(p => p.productId === item.id ? { ...p, detail: product } : p)
        );
      },
      error: (error) => {
        this.importedProducts.update(products => products.filter(p => p.productId !== item.id));
        console.error('Error loading product:', error);
        snackbar$?.show?.('Failed to load product details');
      }
    });
  }

  // Get currently imported product IDs for exclusion in dropdown
  getImportedProductIds(): number[] {
    return this.importedProducts().map(p => p.productId);
  }

  removeImportedProduct(productId: number): void {
    this.importedProducts.update(products => products.filter(p => p.productId !== productId));
  }

  // Get imported products as dropdown items for variant selection
  getImportedProductItems(): LazyDropdownItem[] {
    return this.importedProducts()
      .filter(p => p.detail !== null)
      .map(p => ({
        id: p.productId,
        label: p.detail!.name.english || p.detail!.name.arabic || 'Unnamed Product',
        value: p.productId,
        subtitle: p.detail!.name.arabic && p.detail!.name.english !== p.detail!.name.arabic ? p.detail!.name.arabic : undefined,
      }));
  }

  // Import variant data from selected product into a variation form
  onVariantImportSelection(variationIndex: number, productId: number | null): void {
    if (!productId) {
      return;
    }

    const importedProduct = this.importedProducts().find(p => p.productId === productId);
    if (!importedProduct?.detail?.variants?.[0]) {
      snackbar$?.show?.('Product variant data not found');
      return;
    }

    const sourceVariant = importedProduct.detail.variants[0];
    const targetVariation = this.variations()[variationIndex];
    if (!targetVariation) {
      return;
    }

    // Link this imported product to this variant
    this.importedProducts.update(products =>
      products.map(p => p.productId === productId ? { ...p, variantId: targetVariation.clientId } : p)
    );

    // Determine image to use
    const variantImage = sourceVariant.image?.url || importedProduct.detail.images?.[0]?.url || null;
    const variantImagePath = sourceVariant.image?.path || importedProduct.detail.images?.[0]?.path || null;

    // Populate the variant form
    targetVariation.form.patchValue({
      regularPrice: sourceVariant.regularPrice,
      salePrice: sourceVariant.salePrice,
      costPrice: sourceVariant.costPrice,
      sku: sourceVariant.sku,
      barcode: sourceVariant.barcode,
      itemCode: sourceVariant.itemCode,
      model: sourceVariant.model || importedProduct.detail.model,
      weight: sourceVariant.weight,
      isManageableStock: sourceVariant.isManageableStock,
      stock: sourceVariant.stock,
      height: sourceVariant.height,
      length: sourceVariant.length,
      width: sourceVariant.width,
      status: sourceVariant.status === 1,
      imagePath: variantImage,
    });

    // Store image path as metadata on the form for later use
    if (variantImagePath) {
      (targetVariation.form as any)._importedImagePath = variantImagePath;
    }

    snackbar$?.show?.('Variant data imported successfully');
  }

  // Get the selected product ID for a specific variant
  getVariantImportedProductId(variationIndex: number): number | null {
    const targetVariation = this.variations()[variationIndex];
    if (!targetVariation) {
      return null;
    }

    const importedProduct = this.importedProducts().find(p => p.variantId === targetVariation.clientId);
    return importedProduct?.productId || null;
  }



  editVideoOrder(index: number): void {
    const video = this.productVideos$.value[index];
    if (!video) return;

    dialog$.input({
      title: 'Edit Video Order',
      addButtonText: 'Update',
      formBuilder: formBuilder({
        inputs: {
          order: {
            control: formControl<number>(video.order, Validators.required),
            label: 'Order',
          },
        },
      }),
    })
      .afterSubmit()
      .pipe(
        take(1),
        tap((result: any) => {
          const videos = [...this.productVideos$.value];
          videos[index] = { ...video, order: result.order };
          this.productVideos$.next(videos);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.initializeProductData();
    this.setCategoryChangeListener();
    this.setAttributeItemsChangeListener();
  }

  // Step navigation methods
  canProceedToNextStep(): boolean {
    return this.validateCurrentStep();
  }

  canGoToPreviousStep(): boolean {
    return this.currentStep() > 0;
  }

  canSave(): boolean {
    return this.currentStep() === this.steps().length - 1;
  }

  nextStep(): void {
    if (this.canProceedToNextStep() && this.currentStep() < this.steps().length - 1) {
      this.currentStep.set(this.currentStep() + 1);
      this.stepValidationErrors.set([]);
    }
  }

  // Handle the main action button (Next or Save)
  onMainAction(event: ButtonClickEvent): void {
    if (this.canSave()) {
      this.onSubmit(event);
    } else {
      // For Next button, validate current step first
      if (this.canProceedToNextStep()) {
        this.nextStep();
      }
      event.setLoading(false); // Reset loading state for next button
    }
  }

  // Close dialog method
  closeDialog(): void {
    this.dialog.close();
  }

  previousStep(): void {
    if (this.canGoToPreviousStep()) {
      this.currentStep.set(this.currentStep() - 1);
      this.stepValidationErrors.set([]);
    }
  }

  goToStep(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.steps().length) {
      // Validate all previous steps before allowing navigation
      let canNavigate = true;
      for (let i = 0; i < stepIndex; i++) {
        if (!this.validateStep(i)) {
          canNavigate = false;
          break;
        }
      }

      if (canNavigate) {
        this.currentStep.set(stepIndex);
        this.stepValidationErrors.set([]);
      }
    }
  }

  // Update steps based on product type
  private updateStepsConfiguration(): void {
    const isVariable = this.isVariable();
    const isAdmin = this.data.isAdmin;

    if (isVariable) {
      // Variable products: show all steps
      const steps = [
        { label: 'Information', key: 'information' },
        { label: 'Content', key: 'media' },
        { label: 'Specification', key: 'specification' },
        { label: 'Variants', key: 'variants' }
      ];

      // Only add Related Products step for admins
      if (isAdmin) {
        steps.push({ label: 'People also buy', key: 'related-products' });
      }

      this.steps.set(steps);
    } else {
      // Simple products: no Variants step
      const steps = [
        { label: 'Information', key: 'information' },
        { label: 'Content', key: 'media' },
        { label: 'Specification', key: 'specification' }
      ];

      // Only add Related Products step for admins
      if (isAdmin) {
        steps.push({ label: 'People also buy', key: 'related-products' });
      }

      this.steps.set(steps);
    }

    // Reset to first step if current step is beyond the new step count
    if (this.currentStep() >= this.steps().length) {
      this.currentStep.set(0);
    }
  }

  // Step validation methods
  validateCurrentStep(): boolean {
    return this.validateStep(this.currentStep());
  }

  validateStep(stepIndex: number): boolean {
    const errors: string[] = [];
    const currentSteps = this.steps();

    if (stepIndex >= currentSteps.length) {
      return true;
    }

    const stepKey = currentSteps[stepIndex].key;

    switch (stepKey) {
      case 'information':
        return this.validateInformationStep(errors);
      case 'media':
        return this.validateMediaStep();
      case 'specification':
        return this.validateSpecificationStep(errors);
      case 'variants':
        return this.validateVariantsStep(errors);
      case 'related-products':
        return this.validateRelatedProductsStep();
      default:
        return true;
    }
  }

  private validateInformationStep(errors: string[]): boolean {
    this.basicInfo.formGroup.markAllAsTouched();
    this.categoriesFormGroup.markAllAsTouched();

    const basicData = this.basicInfo.getValue();
    if (!basicData) {
      errors.push('Please fill all required fields in Information');
      this.stepValidationErrors.set(errors);
      return false;
    }

    if (!this.categoriesFormGroup.valid) {
      errors.push('Please select required categories');
      this.stepValidationErrors.set(errors);
      return false;
    }

    if (this.isBundle()) {
      const components = this.bundleComponents();
      const componentVariantIds = components.map((component) => component.componentVariantId);
      if (components.length === 0) {
        errors.push('Please add at least one bundle component');
      }
      if (components.some((component) => !Number.isInteger(component.quantity) || component.quantity <= 0)) {
        errors.push('Bundle component quantities must be positive whole numbers');
      }
      if (components.some((component) => !Number.isFinite(component.unitPrice))) {
        errors.push('Every bundle component must have a valid unit price');
      }
      if (new Set(componentVariantIds).size !== componentVariantIds.length) {
        errors.push('A component variant can only be added once');
      }
      const bundleVariation = this.variations()[0];
      const bundleRegularPrice = bundleVariation?.form.controls.regularPrice.value;
      const bundleSalePrice = bundleVariation?.form.controls.salePrice.value;
      const effectiveBundlePrice = bundleSalePrice ?? bundleRegularPrice;
      const normalizedBundlePrice = effectiveBundlePrice == null
        ? null
        : Math.round(effectiveBundlePrice * 100) / 100;
      if (normalizedBundlePrice != null && this.bundleComponentPriceTotal() !== normalizedBundlePrice) {
        errors.push('Bundle component prices multiplied by quantity must equal the Bundle Price');
      }
      if (errors.length > 0) {
        this.stepValidationErrors.set(errors);
        return false;
      }
    }

    // Simple and bundle products use the single variation form shown in this step.
    if (!this.isVariable()) {
      this.variations().forEach((variation, index) => {
        variation.form.markAllAsTouched();
        this.expandedVariants[index] = !variation.form.valid;
      });

      const areVariationsValid = this.variations().every((v) => v.form.valid);
      if (!areVariationsValid) {
        errors.push(this.isBundle()
          ? 'Please enter the required SKU and Bundle Price in Bundle Details'
          : 'Please fill all required fields in Variation');
        this.stepValidationErrors.set(errors);
        return false;
      }
    }

    this.stepValidationErrors.set([]);
    return true;
  }

  private validateMediaStep(): boolean {
    // Media step is optional, always valid
    this.stepValidationErrors.set([]);
    return true;
  }

  private validateSpecificationStep(errors: string[]): boolean {
    this.specifications().forEach((spec) => spec.markAllAsTouched());

    const areSpecificationsValid = this.specifications().every((spec) => spec.valid);
    if (!areSpecificationsValid) {
      errors.push('Please fill all required fields in Specifications');
      this.stepValidationErrors.set(errors);
      return false;
    }

    this.stepValidationErrors.set([]);
    return true;
  }

  private validateVariantsStep(errors: string[]): boolean {
    this.attributes().forEach((attrSet) => attrSet.markAllAsTouched());
    this.variations().forEach((variation, index) => {
      variation.form.markAllAsTouched();
      this.expandedVariants[index] = !variation.form.valid;
    });

    const areAttributesValid = this.attributes().every((attrSet) => attrSet.valid);
    if (!areAttributesValid) {
      errors.push('Please fill all required fields in Attribute Sets');
      this.stepValidationErrors.set(errors);
      return false;
    }

    const currentVariations = this.variations();
    const areVariationsValid = currentVariations.every((variation) => variation.form.valid);
    if (!areVariationsValid) {
      errors.push('Please fill all required fields in Variations');
      this.stepValidationErrors.set(errors);
      return false;
    }

    if (currentVariations.length === 0) {
      errors.push('Please add at least one variation');
      this.stepValidationErrors.set(errors);
      return false;
    }

    this.stepValidationErrors.set([]);
    return true;
  }

  private validateRelatedProductsStep(): boolean {
    // Related products step is optional, always valid
    this.stepValidationErrors.set([]);
    return true;
  }

  constructor() {
    super();

    effect(() => {
      const error = this.submitError();
      if (error) snackbar$?.show?.(error);
    });

    // Reset scroll to top whenever the current step changes.
    // We use a microtask to ensure the view has updated before manipulating scrollTop.
    effect(() => {
      const step = this.currentStep();
      Promise.resolve().then(() => {
        try {
          const host = this.scrollContainerRef?.nativeElement;
          if (!host) return;

          // If host itself is scrollable, reset it
          if (host.scrollHeight > host.clientHeight) {
            host.scrollTop = 0;
            return;
          }

          // Otherwise search for the first descendant that is scrollable
          const descendants = Array.from(host.querySelectorAll<HTMLElement>('*'));
          for (const d of descendants) {
            try {
              const style = getComputedStyle(d);
              if (/(auto|scroll)/.test(style.overflowY || '') || d.scrollHeight > d.clientHeight) {
                d.scrollTop = 0;
                break;
              }
            } catch { }
          }
        } catch { }
      });
      // no return value needed
    });
  }

  // Expose FormControl instances for template bindings
  get englishDescriptionControl() {
    return this.descriptionsForm.controls['english'] as any;
  }

  get arabicDescriptionControl() {
    return this.descriptionsForm.controls['arabic'] as any;
  }

  // Keep references to editor instances so we can update them if the form value changes after editor init
  private englishEditor: any;
  private arabicEditor: any;

  // Populate English quill editor when it's created
  onEnglishEditorCreated(editor: any) {
    this.englishEditor = editor;
    const val = this.englishDescriptionControl?.value;
    if (val) {
      this.setEditorContent(editor, val, false);
    }

    // Ensure editor stays in sync if the control is patched later
    this.englishDescriptionControl?.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v: any) => {
      if (!this.englishEditor) return;
      this.setEditorContent(this.englishEditor, v || '', false);
    });
  }

  // Populate Arabic quill editor on creation and apply RTL settings
  onArabicEditorCreated(editor: any) {
    this.arabicEditor = editor;
    // First apply RTL transformations
    this.activateRTL(editor);

    const val = this.arabicDescriptionControl?.value;
    if (val) {
      this.setEditorContent(editor, val, true);
    }

    // Keep Arabic editor in sync with control value changes
    this.arabicDescriptionControl?.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v: any) => {
      if (!this.arabicEditor) return;
      // Apply RTL each time in case attributes were reset
      this.activateRTL(this.arabicEditor);
      this.setEditorContent(this.arabicEditor, v || '', true);
    });
  }

  // Helper that sets editor content and reapplies it on the next microtask to avoid race conditions
  private setEditorContent(editor: any, val: string, rtl: boolean) {
    try {
      if (editor.clipboard && typeof editor.clipboard.dangerouslyPasteHTML === 'function') {
        editor.clipboard.dangerouslyPasteHTML(val);
      } else if (typeof editor.setContents === 'function') {
        editor.setContents(val);
      } else if (editor.root) {
        editor.root.innerHTML = val;
      }

      // Microtask fallback to ensure content is applied if the value was patched after editor init
      Promise.resolve().then(() => {
        try {
          if (editor.clipboard && typeof editor.clipboard.dangerouslyPasteHTML === 'function') {
            editor.clipboard.dangerouslyPasteHTML(val);
          } else if (typeof editor.setContents === 'function') {
            editor.setContents(val);
          } else if (editor.root) {
            editor.root.innerHTML = val;
            if (rtl) {
              editor.root.setAttribute('dir', 'rtl');
              editor.root.style.direction = 'rtl';
              editor.root.style.textAlign = 'right';
            }
          }
        } catch { }
      });
    } catch { }
  }

  private setCategoryChangeListener() {
    this.categoriesFormGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        tap((v) => {
          const ids = [v.primaryCategory, ...(v.secondaryCategories ?? [])].filter((i) => i !== undefined);
          this.loadAttributes(ids);
        }),
      )
      .subscribe();
  }

  private loadAttributes(categories: number[]): void {
    if (categories.length === 0) {
      this.attributeItems$.next([]);
      return;
    }

    this.data.apiService
      .getAttributeItemRecords(categories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((records) => this.attributeItems$.next(records));
  }

  private loadAttributesAndReconstruct(categories: number[], variants: VariantEntity[], specifications?: ProductAttributeDetail[]): void {
    if (categories.length === 0) {
      this.attributeItems$.next([]);
      return;
    }

    this.data.apiService
      .getAttributeItemRecords(categories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((records) => {
        this.attributeItems$.next(records);
        // Only reconstruct if we have variants with attributes
        if (variants.length > 0 && variants.some(v => v.attributes.length > 0)) {
          this.reconstructAttributeFormsFromVariants(variants);
        }
        // Reconstruct specifications if provided
        if (specifications && specifications.length > 0) {
          this.reconstructSpecificationForms(specifications);
        }
      });
  }

  private setAttributeItemsChangeListener(): void {
    this.attributeItems$.subscribe((items) => this.checkAttributeUsagesValidation(items));
  }

  private checkAttributeUsagesValidation(items: ItemRecord<number>[]): void {
    const validAttributeIds = new Set(items.map((item) => item.value));

    // Reset specifications with invalid attributeIds
    this.specifications().forEach((specification) => {
      const { attributeId } = specification.value;
      if (!attributeId || !validAttributeIds.has(attributeId)) {
        specification.patchValue({ attributeId: undefined, termId: undefined });
      }
    });

    // Reset attributes with invalid attributeIds
    this.attributes().forEach((attribute) => {
      const { attributeId } = attribute.value;
      if (!attributeId || !validAttributeIds.has(attributeId)) {
        attribute.patchValue({ attributeId: undefined, termIds: undefined });
      }
    });
  }

  private initializeProductData(): void {
    const product = this.data.product;

    if (!product) {
      // For new products, default to Simple product (type = 0)
      this.basicInfo.patchValue({ type: 0 });
      this.isVariable.set(false);
      this.isBundle.set(false);
      this.updateStepsConfiguration();
      this.setupProductTypeListener();
      this.createVariationForm();
      return;
    }

    // Product data is already loaded, use it directly
    this.relatedProducts$.next(product.relatedProducts);

    this.productImages$.next(product.images);
    this.productVideos$.next(product.externalVideos);

    this.categoriesFormGroup.patchValue({
      primaryCategory: product.primaryCategory?.id,
      secondaryCategories: product.categories.map((c) => c.id),
    });

    const categoryIds = [product.primaryCategory?.id, ...product.categories.map(c => c.id)].filter(id => id !== undefined);
    this.loadAttributesAndReconstruct(categoryIds, product.variants, product.attributes);

    this.originalVariants = product.variants;

    product.variants.forEach((variant) => {
      this.createVariationForm(variant);
    });

    this.expandedVariants = this.variations().map(() => false);

    this.basicInfo.patchValue({
      englishName: product.name.english,
      arabicName: product.name.arabic,
      // descriptions moved to separate reactive group (keep builder controls untouched)
      brandId: product.brand?.id,
      isFeatured: product.isFeatured,
      isPreOrder: product.isPreOrder ?? false,
      keepTop: product.keepTop,
      type: product.type,
      slug: product.slug,
      status: product.status === 1,
      limitedToOnePurchases: product.limitedToOnePurchases,
      vendorId: product.vendorId,
    });
    this.preOrderAvailableOn.setValue(product.preOrderAvailableOn ?? null);

    // Populate the separate descriptions form
    this.descriptionsForm.patchValue({
      english: product.description?.english,
      arabic: product.description?.arabic,
    });

    // Set the initial product type state and update step configuration
    const isVariable = product.type === 1;
    this.isVariable.set(isVariable);
    this.isBundle.set(product.type === 2);
    this.bundleStockMode.set(product.bundleStockMode ?? BundleStockMode.Manual);
    this.initializeBundleComponents(product);
    this.updateStepsConfiguration();

    // Setup listener for future changes
    this.setupProductTypeListener();
  }

  setupProductTypeListener() {
    // Subscribe to value changes for future updates
    this.basicInfo.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const isVariable = value === 1;
      const isBundle = value === 2;
      this.isVariable.set(isVariable);
      this.isBundle.set(isBundle);

      if (!isBundle) {
        this.bundleComponents.set([]);
        this.bundleStockMode.set(BundleStockMode.Manual);
      }

      // Update step configuration based on product type
      this.updateStepsConfiguration();

      if (!isVariable) {
        // Switching to Simple: keep a single variation for simple product
        this.variations.set([]);
        this.createVariationForm();
      } else {
        // Switching to Variable: clear any simple variant and generate combinations
        this.variations.set([]);
        // Only attempt to generate combinations if there are attributes with at least one term selected
        const hasValidAttributes = this.attributes().some(
          (attr) => (attr.controls.termIds.value || []).length > 0 && !!attr.controls.attributeId.value,
        );
        if (hasValidAttributes) {
          this.updateAllTermCombinations();
        }
      }
    });
  }

  private initializeBundleComponents(product: ProductEntity): void {
    if (product.type !== 2 || !product.bundleComponents?.length) {
      this.bundleComponents.set([]);
      return;
    }

    this.bundleComponents.set(product.bundleComponents.map((component) => ({
      productId: component.productId,
      productName: component.productName.english || component.productName.arabic || 'Unnamed Product',
      componentVariantId: component.componentVariantId,
      quantity: component.quantity,
      unitPrice: component.unitPrice,
      imageUrl: undefined,
      variants: [{
        id: component.componentVariantId,
        sku: component.sku,
        regularPrice: component.unitPrice,
        salePrice: null,
      }],
      stockLoaded: false,
    })));

    [...new Set(product.bundleComponents.map((component) => component.productId))].forEach((productId) => {
      this.data.apiService.get(productId).pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: (componentProduct) => {
          this.bundleComponents.update((components) => components.map((component) =>
            component.productId === productId
              ? {
                ...component,
                productName: componentProduct.name.english || componentProduct.name.arabic || component.productName,
                imageUrl: componentProduct.images?.[0]?.url,
                variants: componentProduct.variants,
                stockLoaded: true,
              }
              : component));
        },
        error: () => snackbar$?.show?.(`Failed to load component product ${productId}`),
      });
    });
  }

  createSpecificationForm(sp?: ProductAttributeDetail): void {
    const group = formGroup({
      attributeId: formControl<number>(sp?.attributeId, Validators.required),
      termId: formControl<number>(sp?.termId, Validators.required),
    });

    group.controls.attributeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      group.controls.termId.reset();
    });

    this.specifications.update((value) => [...value, group]);
  }

  removeSpecification(index: number): void {
    this.specifications.update((prev) => prev.filter((_, idx) => idx !== index));
  }

  createVariationForm(variation?: VariantEntity, attrs?: ProductAttribute[]) {
    const form = VariationFormComponent.createForm(variation);
    const attributes: ProductAttribute[] | ProductAttributeDetail[] = variation?.attributes ?? attrs ?? [];

    const variationForm: Variation = {
      clientId: variation?.id ? `var_${variation.id}` : `temp_${Date.now()}_${Math.random()}`,
      variationId: variation?.id,
      form,
      attributes
    };

    this.variations.update((v) => [...v, variationForm]);
  }

  createAttributeForm(attribute?: ProductAttributeDetail): void {
    const existing = this.attributes().find((set) => set.controls.attributeId.getValue() === attribute?.attributeId);

    if (existing) {
      const termIds = existing.controls.termIds;
      const currentTerms = termIds.getValue() || [];
      if (attribute?.termId && !currentTerms.includes(attribute.termId)) {
        termIds.patchValue([...currentTerms, attribute.termId]);
      }
      return;
    }

    const group = formGroup({
      attributeId: formControl<number>(attribute?.attributeId, Validators.required),
      termIds: formControl<number[]>(attribute?.termId ? [attribute.termId] : [], Validators.required),
    });

    group.controls.attributeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      group.controls.termIds.reset();
    });

    this.attributes.update((value) => [...value, group]);
  }

  private reconstructAttributeFormsFromVariants(variants: VariantEntity[]): void {
    // Group all variant attributes by attributeId to reconstruct the original attribute-term pairs
    const attributeMap = new Map<number, Set<number>>();

    variants.forEach((variant) => {
      variant.attributes.forEach((attr) => {
        if (!attributeMap.has(attr.attributeId)) {
          attributeMap.set(attr.attributeId, new Set());
        }
        attributeMap.get(attr.attributeId)!.add(attr.termId);
      });
    });

    // Create attribute forms with all collected terms for each attribute
    attributeMap.forEach((termIds, attributeId) => {
      const group = formGroup({
        attributeId: formControl<number>(attributeId, Validators.required),
        termIds: formControl<number[]>(Array.from(termIds), Validators.required),
      });

      group.controls.attributeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        group.controls.termIds.reset();
      });

      this.attributes.update((value) => [...value, group]);
    });
  }

  private reconstructSpecificationForms(specifications: ProductAttributeDetail[]): void {
    // Create specification forms from the provided specifications
    specifications.forEach((spec) => {
      const group = formGroup({
        attributeId: formControl<number>(spec.attributeId, Validators.required),
        termId: formControl<number>(spec.termId, Validators.required),
      });

      group.controls.attributeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        group.controls.termId.reset();
      });

      this.specifications.update((value) => [...value, group]);
    });
  }

  removeAttributeSet(index: number): void {
    this.attributes.update((prev) => prev.filter((_, idx) => idx !== index));
  }

  getTermItems(field: Specification | Attribute): Observable<ItemRecord<number>[]> {
    const attributeId = field.controls.attributeId.value;

    if (!attributeId) return of([]);

    if (!this.termItemsCache[attributeId]) {
      this.termItemsCache[attributeId] = this.data.apiService
        .getAttributeTermItemRecords(attributeId)
        .pipe(shareReplay(1));
    }

    return this.termItemsCache[attributeId];
  }

  getTermName(attribute: ProductAttribute | ProductAttributeDetail): Observable<string | undefined> {
    // If this is a ProductAttributeDetail (has term property), return the term name directly
    if ('term' in attribute && attribute.term) {
      return of(attribute.term.english || attribute.term.arabic);
    }

    // Otherwise, use the cache lookup for ProductAttribute
    const cache = this.termItemsCache[attribute.attributeId];
    return cache?.pipe(map((terms) => terms.find((term) => term.value === attribute.termId)?.label)) || of(undefined);
  }

  private readonly attributeApi = inject(AttributeApiService);

  addNewAttributeValue(attributeId: number | undefined, event?: ButtonClickEvent): void {
    if (!attributeId) {
      snackbar$?.show?.('Please select an attribute first');
      return;
    }

    // Use the global helper function to open attribute dialog with pre-loaded data
    openAttributeDialog(
      this.attributeApi,
      attributeId,
      event ? (loading) => event.setLoading(loading) : undefined,
      true
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          // Refresh the term items cache for this attribute
          delete this.termItemsCache[attributeId];
          this.termItemsCache[attributeId] = this.data.apiService
            .getAttributeTermItemRecords(attributeId)
            .pipe(shareReplay(1));

          snackbar$?.show?.('Attribute value added successfully');
        }
      });
  }

  updateAllTermCombinations() {
    const validCombinations = this.getCombinations();
    if (!validCombinations) return;

    const existingCombinations = this.variations().map((v) => v.attributes);

    validCombinations.forEach((combination) => {
      const alreadyExists = existingCombinations.some((existing) => this.areCombinationsEqual(combination, existing));

      if (!alreadyExists) {
        this.createVariationForm(undefined, combination);
      }
    });

    this.variations.update((variations) =>
      variations.filter((variation) =>
        validCombinations.some((valid) => this.areCombinationsEqual(valid, variation.attributes)),
      ),
    );
  }

  deleteVariation(index: number) {
    this.variations.update((variations) => {
      return variations.filter((_, idx) => idx !== index);
    });
  }

  private areCombinationsEqual(comb1: ProductAttribute[], comb2: ProductAttribute[]): boolean {
    if (comb1.length !== comb2.length) return false;

    return comb1.every((attr1) => {
      return comb2.some((attr2) => attr1.attributeId === attr2.attributeId && attr1.termId === attr2.termId);
    });
  }

  getCombinations(): ProductAttribute[][] | undefined {
    const termLists: ProductAttribute[][] = [];

    for (const attr of this.attributes()) {
      if (!attr.valid) {
        attr.markAllAsTouched();
        return undefined;
      }

      const attributeId = attr.controls.attributeId.value;
      const termIds = attr.controls.termIds.value || [];

      const terms: ProductAttribute[] = termIds.map((termId) => ({ attributeId, termId }));
      if (terms.length > 0) {
        termLists.push(terms);
      }
    }

    // If no attribute sets have any terms selected, do not create empty combinations
    if (termLists.length === 0) return undefined;

    return this.generateCombinations(termLists);
  }

  private generateCombinations(lists: ProductAttribute[][], prefix: ProductAttribute[] = []): ProductAttribute[][] {
    if (lists.length === 0) return [prefix];

    const [firstList, ...restLists] = lists;
    const combinations: ProductAttribute[][] = [];

    for (const item of firstList) {
      combinations.push(...this.generateCombinations(restLists, [...prefix, item]));
    }

    return combinations;
  }

  toggleVariant(index: number): void {
    this.expandedVariants[index] = !this.expandedVariants[index];
  }

  onSubmit(event: ButtonClickEvent): void {
    event.setLoading(true);

    // Validate all steps before submission
    for (let i = 0; i < this.steps().length; i++) {
      if (!this.validateStep(i)) {
        event.setLoading(false);
        this.currentStep.set(i); // Navigate to the first invalid step
        return;
      }
    }

    const basicData = this.basicInfo.getValue();
    if (!basicData) {
      event.setLoading(false);
      this.submitError.set('Please fill all required fields');
      return;
    }

    const categoriesData = this.categoriesFormGroup.getRawValue();

    const fields = {
      name: {
        english: basicData.englishName,
        arabic: basicData.arabicName,
      },
      primaryCategoryId: categoriesData.primaryCategory,
      slug: basicData.slug,
      description: {
        english: this.descriptionsForm.controls['english'].getRawValue(),
        arabic: this.descriptionsForm.controls['arabic'].getRawValue(),
      },
      brandId: basicData.brandId,
      limitedToOnePurchases: basicData.limitedToOnePurchases,
      type: basicData.type,
      state: basicData.status ? 1 : 0,
      isFeatured: basicData.isFeatured,
      ...(this.data.isAdmin ? {
        isPreOrder: basicData.isPreOrder,
        preOrderAvailableOn: this.preOrderAvailableOn.value || null,
      } : {}),
      keepTop: basicData.keepTop,
      bundleStockMode: this.isBundle() ? this.bundleStockMode() : undefined,
      bundleComponents: this.isBundle()
        ? this.bundleComponents().map(({ componentVariantId, quantity, unitPrice }) => ({
          componentVariantId,
          quantity,
          unitPrice,
        }))
        : undefined,
    };

    const flatFields = {
      vendorId: basicData.vendorId,
      externalVideos: this.productVideos$.value,
      images: this.productImages$.value,
      categories: categoriesData.secondaryCategories || [],
      relatedProducts: this.relatedProducts$.value.map((p) => p.id),
      attributes: this.specifications().map((spec) => spec.controls.termId.value),
    };

    if (!this.data.product || this.data.duplicate) {
      const variants = this.variations().map((v) => this.prepareVariantData(v));

      const createData = {
        fields: fields,
        ...flatFields,
        variants: variants,
      };

      this.data.apiService
        .create(createData)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            event.setLoading(false);
            this.showImportedProductsDeletionDialog();
            this.dialog.close(true);
            snackbar$?.show?.('Product saved successfully');
          },
          error: () => event.setLoading(false),
        });
    } else {
      const allVariations = this.variations();
      const originalIds = this.originalVariants.map((o) => o.id);

      const existingVariants = allVariations
        .filter((v) => v.variationId && originalIds.includes(v.variationId))
        .map((v) => this.prepareVariantData(v));

      const newVariants = allVariations.filter((v) => !v.variationId).map((v) => this.prepareVariantData(v));
      const deletedVariants = originalIds.filter(
        (id) => !allVariations.some((v) => v.variationId && v.variationId === id),
      );

      const updateData = {
        productId: this.data.productId,
        fields: APIHelper.nestValues(fields, false),
        ...APIHelper.nestValues(flatFields, false),
        variants: existingVariants.map((v) => APIHelper.nestValues(v)),
      };

      const updateProductCall = this.data.apiService.update(updateData);
      const apiCalls: Observable<object>[] = [updateProductCall];

      if (newVariants.length > 0) {
        const addVariantsCall = this.data.apiService.addVariants({
          productId: this.data.productId!,
          variants: newVariants,
        });
        apiCalls.push(addVariantsCall);
      }

      if (deletedVariants.length > 0) {
        const deleteVariantsCall = this.data.apiService.deleteVariants(deletedVariants);
        apiCalls.push(deleteVariantsCall);
      }

      forkJoin(apiCalls)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            event.setLoading(false);
            this.showImportedProductsDeletionDialog();
            this.dialog.close(true);
            snackbar$?.show?.('Product saved successfully');
          },
          error: () => event.setLoading(false),
        });
    }
  }

  private prepareVariantData(variation: Variation): VariantUpdate {
    const formValue = variation.form.getRawValue();
    const original = this.originalVariants.find((v) => v.id === variation.variationId);

    // Determine image path to send to backend
    let imagePath;
    if ((variation.form as any)._importedImagePath) {
      // Imported image path stored on form
      imagePath = (variation.form as any)._importedImagePath;
    } else if (typeof formValue.imagePath === 'string') {
      // URL string from existing variant - use original path
      imagePath = original?.image?.path;
    } else {
      // Newly uploaded image object
      imagePath = formValue.imagePath?.imagePath;
    }

    return {
      id: variation.variationId,
      regularPrice: formValue.regularPrice,
      salePrice: formValue.salePrice ?? null,
      discountExpiresAtUtc: formValue.salePrice != null && formValue.discountExpiresAtUtc
        ? new Date(`${formValue.discountExpiresAtUtc}Z`).toISOString() : null,
      costPrice: formValue.costPrice,
      sku: formValue.sku,
      barcode: formValue.barcode,
      itemCode: formValue.itemCode,
      model: formValue.model,
      weight: formValue.weight,
      isManageableStock: formValue.isManageableStock,
      stock: formValue.stock,
      height: formValue.height,
      length: formValue.length,
      width: formValue.width,
      status: formValue.status ? 1 : 0,
      image: imagePath,
      attributes: variation.attributes.map((attr) => ({
        attributeId: attr.attributeId,
        termId: attr.termId,
      })),
    };
  }

  shouldShowShareLink(): boolean {
    const product = this.data.product;
    return !!(product?.shareLink);
  }

  getShareLinkForDisplay(): string {
    return this.data.product?.shareLink || '';
  }

  getShareLinkMessage(): string | null {
    const product = this.data.product;

    if (!product?.shareLink) {
      return null;
    }

    const isApproved = product.approvalStatus === 1;
    const isEnabled = product.status === 1;

    if (isApproved && isEnabled) {
      return null; // No error, link is active
    }

    let reasons = [];
    if (!isApproved) {
      reasons.push('product is not approved');
    }
    if (!isEnabled) {
      reasons.push('product is disabled');
    }

    return `This link is inactive because ${reasons.join(' and ')}.`;
  }

  generateSlug(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
  }

  private isAutoGeneratedSlug(currentSlug: string, englishName: string): boolean {
    // Check if current slug matches what would be auto-generated from previous English name parts
    const expectedSlug = this.generateSlug(englishName);
    return currentSlug === expectedSlug || expectedSlug.startsWith(currentSlug) || currentSlug.startsWith(expectedSlug);
  }

  // Show disable dialog for imported products after save
  private showImportedProductsDeletionDialog(): void {
    const usedProducts = this.importedProducts().filter(p => p.variantId !== null && p.detail);
    if (usedProducts.length === 0) {
      return;
    }

    const productNames = usedProducts.map(p => p.detail!.name.english || p.detail!.name.arabic || 'Unnamed Product');
    const productIds = usedProducts.map(p => p.productId);
    const message = `The following simple products were merged into this variable product: ${productNames.join(' • ')}. Do you want to disable these simple products?`;

    dialog$.prompt({
      title: 'Disable Imported Products?',
      message: message,
      yesButtonText: 'Yes, Disable Products',
      noButtonText: 'No, Keep Enabled',
    }).action(() => this.data.apiService.changeStatus(false, productIds)).subscribe();
  }

  // Helper method to find a category by ID in the nested tree structure
  private findCategoryById(categories: CategoryItem[], targetId: number): CategoryItem | null {
    for (const category of categories) {
      if (category.id === targetId) {
        return category;
      }
      if (category.subCategories && category.subCategories.length > 0) {
        const found = this.findCategoryById(category.subCategories, targetId);
        if (found) return found;
      }
    }
    return null;
  }

  // Get category name by ID
  getCategoryName(categoryId: number | undefined): Observable<string> {
    if (!categoryId) {
      return of('');
    }

    return this.categories$.pipe(
      map(categories => {
        const category = this.findCategoryById(categories, categoryId);
        return category ? (category.name.english || category.name.arabic) : '';
      })
    );
  }

  // Get category names for multiple IDs
  getCategoryNames(categoryIds: number[] | undefined): Observable<string[]> {
    if (!categoryIds || categoryIds.length === 0) {
      return of([]);
    }

    return this.categories$.pipe(
      map(categories => {
        return categoryIds.map(id => {
          const category = this.findCategoryById(categories, id);
          return category ? (category.name.english || category.name.arabic) : '';
        }).filter(name => name !== '');
      })
    );
  }
}
